import * as React from 'react';

import { CloseCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { Modal, Input, Checkbox, Divider, Button } from 'antd';

import { useCurrencies } from 'v2/hooks/useCurrencies';
import type { Currency } from 'v2/interfaces/currency';

type ChooseCurrenciesModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  selectedCurrencies: Currency[];
  addCurrencies: (currencies: Currency[]) => void;
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 600);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

function ChooseCurrenciesModal({
  isOpen,
  closeModal,
  selectedCurrencies: selectedCurrenciesProp = [],
  addCurrencies
}: ChooseCurrenciesModalProps) {
  const isMobile = useIsMobile();
  const [search, setSearch] = React.useState('');
  const currenciesQuery = useCurrencies();
  const [selectedCurrencies, setSelectedCurrencies] = React.useState<Currency[]>([]);

  function handleCurrencyClick(currency: Currency) {
    const selectedCurrenciesIds = selectedCurrencies.map(selectedCurrency => selectedCurrency.id);

    if (selectedCurrenciesIds.includes(currency.id)) {
      setSelectedCurrencies(selectedCurrencies.filter(selectedCurrency => selectedCurrency.id !== currency.id));
      return;
    }

    setSelectedCurrencies([...selectedCurrencies, currency]);
  }

  function handleAddButtonClick() {
    setSelectedCurrencies([]);
    addCurrencies(selectedCurrencies);
    closeModal();
    setTimeout(() => setSearch(''), 500);
  }

  function handleClose() {
    setSelectedCurrencies([]);
    closeModal();
    setTimeout(() => setSearch(''), 500);
  }

  const filteredCurrencies = React.useMemo(() => {
    const noBRLCurrencies = currenciesQuery.data?.filter(currency => currency.code !== 'BRL');
    const notAlreadySelectedCurrencies = noBRLCurrencies?.filter(currency => {
      const selectedCurrenciesIds = selectedCurrenciesProp.map(selectedCurrency => selectedCurrency.id);
      return !selectedCurrenciesIds.includes(currency.id);
    });
    if (!search) return notAlreadySelectedCurrencies;
    return notAlreadySelectedCurrencies.filter(currency => {
      const codeMatches = currency.code.toLowerCase().includes(search.toLowerCase());
      const nameMatches = currency.name.toLowerCase().includes(search.toLowerCase());
      return codeMatches || nameMatches;
    });
  }, [currenciesQuery.data, search, selectedCurrenciesProp]);

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      width={isMobile ? '100%' : 600}
      style={isMobile ? { top: 0, margin: 0, maxWidth: '100vw', height: '100vh' } : undefined}
      styles={!isMobile ? { body: { height: 400, overflowY: 'auto' } } : undefined}
      title={<strong>Escolher moedas</strong>}
      footer={
        <Button
          id='add-currencies-button'
          type='primary'
          onClick={handleAddButtonClick}
          disabled={!selectedCurrencies.length}
        >
          Adicionar moedas {!!selectedCurrencies.length && `(${selectedCurrencies.length})`}
        </Button>
      }
    >
      <Input
        id='search-currencies'
        prefix={<SearchOutlined />}
        suffix={
          !!search && (
            <CloseCircleOutlined
              className='cursor-pointer text-base'
              aria-label='Limpar busca'
              onClick={() => setSearch('')}
            />
          )
        }
        placeholder='Buscar moedas'
        value={search}
        onChange={event => setSearch(event.target.value)}
      />
      <div className='mt-2'>
        {filteredCurrencies?.map(currency => (
          <React.Fragment key={currency.id}>
            <div
              className='flex cursor-pointer items-center gap-3 px-1 py-2 hover:bg-gray-50'
              onClick={() => handleCurrencyClick(currency)}
            >
              <Checkbox id={`currency-${currency.code}`} checked={selectedCurrencies.includes(currency)} />
              <label htmlFor={`currency-${currency.code}`} className='cursor-pointer'>
                {currency.code} ({currency.name})
              </label>
            </div>
            <Divider className='my-0!' />
          </React.Fragment>
        ))}
        {filteredCurrencies?.length === 0 && <div className='py-4 text-center'>Nenhuma moeda encontrada</div>}
      </div>
    </Modal>
  );
}

export { ChooseCurrenciesModal };
